import React, { memo, useMemo } from 'react'
import { useSelector } from 'react-redux'
import { EmptyWebhookSubscriptions } from 'src/components/webhook/grid/EmptyWebhookSubscriptions'
import { GridTable } from 'src/components/shared/grid/GridTable'
import { webhookSubscriptionPageSizeOptions } from 'src/constants/webhook/constants'
import { buildWebhookSubscriptionColumns } from 'src/constants/webhook/webhookSubscriptionsGrid'
import type { WebhookSubscription } from 'src/interfaces/webhook'
import {
  getWebhookSaving,
  getWebhookSubscriptions,
  getWebhookSubscriptionsLoading,
} from 'src/selectors/webhook'
import { sortSubscriptionsByNewest } from 'src/utils/webhookSubscriptionList'
import { useGridPagination } from 'src/utils/hooks/grid/useGridPagination'

interface Props {
  canManage: boolean
  onEditSubscription(subscription: WebhookSubscription): void
  onToggleSubscription(subscription: WebhookSubscription): void
}

const getRowId = (row: WebhookSubscription) => {
  return row.id
}

const WebhookSubscriptionsTableComponent = ({
  canManage,
  onEditSubscription,
  onToggleSubscription,
}: Props) => {
  const subscriptions = useSelector(getWebhookSubscriptions)
  const isLoading = useSelector(getWebhookSubscriptionsLoading)
  const isSaving = useSelector(getWebhookSaving)

  const { paginationModel, setPaginationModel } = useGridPagination(
    webhookSubscriptionPageSizeOptions,
  )

  // `findAllByPartnerId` has no ORDER BY, so ordering is the client's job — on a COPY, since
  // this array is read straight out of Redux state.
  const sortedSubscriptions = useMemo(() => {
    return sortSubscriptionsByNewest(subscriptions)
  }, [subscriptions])

  // A fresh array each render would reset the grid's column state, so the factory call is
  // memoized.
  const subscriptionColumns = useMemo(() => {
    return buildWebhookSubscriptionColumns(
      onEditSubscription,
      onToggleSubscription,
      isSaving,
      canManage,
    )
  }, [onEditSubscription, onToggleSubscription, isSaving, canManage])

  // No persisted visibility model here: the grid's own column menu can hide the Subscription ID
  // column for the visit. The deliveries grid is the one that persists a visibility model.
  return (
    <GridTable<WebhookSubscription>
      rows={sortedSubscriptions}
      columns={subscriptionColumns}
      getRowId={getRowId}
      isLoading={isLoading}
      sortingMode="client"
      paginationMode="client"
      paginationModel={paginationModel}
      onPaginationModelChange={setPaginationModel}
      pageSizeOptions={webhookSubscriptionPageSizeOptions}
      EmptyElement={EmptyWebhookSubscriptions}
      tableDataTestId="webhook-subscriptions-table"
      rowDataTestIdPrefix="webhook-subscriptions"
    />
  )
}

export const WebhookSubscriptionsTable = memo(WebhookSubscriptionsTableComponent)
